import { useRef, useState, ReactNode } from 'react';
import { StoreCategory, StoreColor } from '../../models';
import { CatalogFilters } from '../../hooks/useCatalogFilters';
import { useVariationCatalog } from '../../hooks/useVariationCatalog';
import { FILTER_CHIP_ACTIVE_CLASS, FILTER_CHIP_CLASS, FILTER_CHIP_INACTIVE_CLASS } from './filterChipStyles';
import { VariationFilter } from '../ui/VariationFilter';
import { useDragScroll } from '../../hooks/useDragScroll';
import { pickFeaturedCategories } from '../../utils/featuredCategories';
import { PriceRangeSlider } from '../ui/PriceRangeSlider';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export const PRICE_MIN = 0;
export const PRICE_MAX_FALLBACK = 500;

const CATEGORY_ICON_COUNT = 11;
const COLORS_COLLAPSED = 8;

export const GENDER_OPTIONS: { label: string; value: number | undefined }[] = [
  { label: 'Todos', value: undefined },
  { label: 'Meninos', value: 1 },
  { label: 'Meninas', value: 2 },
  { label: 'Unissex', value: 4 },
];

const GENDER_ACTIVE_CLASS: Record<number, string> = {
  1: 'bg-olivine/15 border-olivine text-olivine-dark font-medium',
  2: 'bg-champagne border-chocolate text-chocolate-dark font-medium',
  4: 'bg-damasco/20 border-damasco text-chocolate-dark font-medium',
};

export function categoryIconSrc(index: number): string {
  return `/images/categories/icon-${(index % CATEGORY_ICON_COUNT) + 1}.webp`;
}

export function genderLabel(value: number | undefined): string | undefined {
  return GENDER_OPTIONS.find((o) => o.value === value && o.value !== undefined)?.label;
}

export function GenderChips({ filters }: { filters: CatalogFilters }) {
  return (
    <>
      {GENDER_OPTIONS.map((opt) => {
        const isActive = filters.gender === opt.value;
        const activeClass = (opt.value !== undefined && GENDER_ACTIVE_CLASS[opt.value]) || FILTER_CHIP_ACTIVE_CLASS;
        return (
          <button
            key={opt.label}
            type="button"
            aria-pressed={isActive}
            onClick={() => filters.handleGenderChange(opt.value)}
            className={[FILTER_CHIP_CLASS, 'whitespace-nowrap', isActive ? activeClass : FILTER_CHIP_INACTIVE_CLASS].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </>
  );
}

export function PromotionChip({ filters }: { filters: CatalogFilters }) {
  const locked = filters.promotionLocked;
  return (
    <button
      type="button"
      disabled={locked}
      aria-pressed={filters.hasPromotion}
      onClick={() => filters.handlePromotionChange(!filters.hasPromotion)}
      title={locked ? 'Filtro fixo durante a campanha promocional' : undefined}
      className={[
        FILTER_CHIP_CLASS,
        'whitespace-nowrap',
        filters.hasPromotion ? FILTER_CHIP_ACTIVE_CLASS : FILTER_CHIP_INACTIVE_CLASS,
        locked ? 'cursor-default' : '',
      ].join(' ')}
    >
      Promoção
    </button>
  );
}

function Section({ title, badge, aside, children }: { title: string; badge?: number; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="pb-5 lg:pb-3 border-b border-champagne last:border-b-0 last:pb-0">
      <h3 className="flex items-center gap-2 text-sm font-bold text-text-dark mb-3 lg:mb-2">
        {title}
        {badge !== undefined && badge > 0 && (
          <span className="bg-chocolate-dark text-white text-[11px] min-w-4 h-4 px-1 rounded-full inline-flex items-center justify-center font-semibold">
            {badge}
          </span>
        )}
        {aside && <span className="ml-auto text-sm font-normal text-text-medium">{aside}</span>}
      </h3>
      {children}
    </section>
  );
}

function SearchBox({ value, onChange, placeholder, autoFocus }: { value: string; onChange: (v: string) => void; placeholder: string; autoFocus?: boolean }) {
  return (
    <div className="relative mb-2">
      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-light pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-champagne-dark bg-champagne-light focus:outline-none focus:border-chocolate focus:bg-white"
      />
    </div>
  );
}

function ShowMoreButton({ expanded, hidden, onToggle }: { expanded: boolean; hidden: number; onToggle: () => void }) {
  if (!expanded && hidden <= 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mt-2 lg:mt-1.5 inline-flex items-center min-h-[44px] lg:min-h-0 text-sm text-chocolate-dark font-medium hover:text-chocolate transition-colors"
    >
      {expanded ? 'Ver menos' : `Ver mais ${hidden}`}
    </button>
  );
}

function ColorList({ colors, filters }: { colors: StoreColor[]; filters: CatalogFilters }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded
    ? colors
    : colors.filter((c, i) => i < COLORS_COLLAPSED || c.id === filters.colorId);
  const hidden = colors.length - visible.length;

  return (
    <div>
      <div className="grid grid-cols-4 gap-x-2 gap-y-3 lg:gap-y-1.5">
        {visible.map((c) => {
          const selected = filters.colorId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              title={c.name}
              aria-pressed={selected}
              aria-label={c.name}
              onClick={() => filters.handleColorChange(selected ? undefined : c.id)}
              className="flex flex-col items-center justify-start gap-1 lg:gap-0.5 group min-h-[44px] lg:min-h-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
            >
              <span
                className={`w-9 h-9 lg:w-7 lg:h-7 rounded-full border-2 block transition-all ${
                  selected
                    ? 'border-chocolate scale-110 shadow-md'
                    : 'border-champagne-dark group-hover:border-chocolate/60 group-hover:scale-105'
                }`}
                style={{ backgroundColor: c.hexadecimal }}
              />
              <span className={`text-xs leading-tight text-center truncate max-w-full ${selected ? 'text-chocolate-dark font-medium' : 'text-text-medium'}`}>
                {c.name}
              </span>
            </button>
          );
        })}
      </div>
      <ShowMoreButton expanded={expanded} hidden={hidden} onToggle={() => setExpanded((v) => !v)} />
    </div>
  );
}

function CategoryChips({ categories, filters }: { categories: StoreCategory[]; filters: CatalogFilters }) {
  const [expanded, setExpanded] = useState(false);
  const featuredIds = new Set(pickFeaturedCategories(categories).map((c) => c.id));
  const visible = expanded
    ? categories
    : categories.filter((c) => featuredIds.has(c.id) || filters.categoryIds.includes(c.id));
  const hidden = categories.length - visible.length;

  const toggle = (id: number) => {
    filters.handleCategoryChange(
      filters.categoryIds.includes(id) ? filters.categoryIds.filter((c) => c !== id) : [...filters.categoryIds, id],
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {visible.map((category) => {
          const selected = filters.categoryIds.includes(category.id);
          return (
            <button
              key={category.id}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(category.id)}
              className={[FILTER_CHIP_CLASS, selected ? FILTER_CHIP_ACTIVE_CLASS : FILTER_CHIP_INACTIVE_CLASS].join(' ')}
            >
              {category.name}
            </button>
          );
        })}
      </div>
      <ShowMoreButton expanded={expanded} hidden={hidden} onToggle={() => setExpanded((v) => !v)} />
    </div>
  );
}

interface FilterPanelProps {
  colors: StoreColor[];
  filters: CatalogFilters;
  priceMax: number;
  categories?: StoreCategory[];
}

export function CatalogFilterPanel({ colors, filters, priceMax, categories }: FilterPanelProps) {
  const minValue = filters.minPriceInput !== '' ? Number(filters.minPriceInput) : PRICE_MIN;
  const maxValue = filters.maxPriceInput !== '' ? Number(filters.maxPriceInput) : priceMax;

  return (
    <div className="flex flex-col gap-5 lg:gap-3">
      <Section title="Para quem" badge={filters.gender !== undefined ? 1 : 0}>
        <div className="flex flex-wrap gap-2">
          <GenderChips filters={filters} />
          <PromotionChip filters={filters} />
        </div>
      </Section>

      {categories && categories.length > 0 && (
        <Section title="Categoria" badge={filters.categoryIds.length}>
          <CategoryChips categories={categories} filters={filters} />
        </Section>
      )}

      <Section title="Idade e tamanho" badge={filters.variationValues.length}>
        <VariationFilter
          selectedTypeId={filters.variationTypeId}
          onTypeChange={filters.handleVariationTypeChange}
          values={filters.variationValues}
          onChange={filters.handleVariationChange}
          onSelect={filters.handleVariationSelect}
        />
      </Section>

      {colors.length > 0 && (
        <Section title="Cor" badge={filters.colorId !== undefined ? 1 : 0}>
          <ColorList colors={colors} filters={filters} />
        </Section>
      )}

      <Section
        title="Preço"
        badge={filters.minPriceInput !== '' || filters.maxPriceInput !== '' ? 1 : 0}
        aside={`R$ ${minValue} a R$ ${maxValue}`}
      >
        <PriceRangeSlider
          min={PRICE_MIN}
          max={priceMax}
          minValue={minValue}
          maxValue={maxValue}
          onMinChange={(value) => filters.handleMinPriceChange(String(value))}
          onMaxChange={(value) => filters.handleMaxPriceChange(String(value))}
        />
      </Section>
    </div>
  );
}

function normalize(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function AllCategoriesDialog({ categories, filters, onClose }: { categories: StoreCategory[]; filters: CatalogFilters; onClose: () => void }) {
  useBodyScrollLock(true);
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true, onClose);

  const term = normalize(query.trim());
  const visible = categories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => !term || normalize(category.name).includes(term));

  const toggle = (id: number) => {
    filters.handleCategoryChange(
      filters.categoryIds.includes(id) ? filters.categoryIds.filter((c) => c !== id) : [...filters.categoryIds, id],
    );
  };

  return (
    <div className="fixed inset-0 z-sheet flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Todas as categorias"
        className="relative w-full sm:max-w-md max-h-[80vh] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-xl focus:outline-none"
      >
        <div className="flex items-center justify-between pl-4 pr-1 pt-2 pb-1">
          <h2 className="font-display font-semibold text-text-dark text-lg">Categorias</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="w-11 h-11 flex items-center justify-center rounded-full text-text-medium hover:text-text-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-4">
          <SearchBox value={query} onChange={setQuery} placeholder="Buscar categoria" autoFocus />
        </div>
        <ul className="flex-1 overflow-y-auto overscroll-y-contain px-3 pb-2 flex flex-col gap-1">
          {visible.length === 0 && <li className="text-sm text-text-medium px-2 py-3">Nenhuma categoria com esse nome.</li>}
          {visible.map(({ category, index }) => {
            const selected = filters.categoryIds.includes(category.id);
            return (
              <li key={category.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggle(category.id)}
                  className={[
                    'w-full flex items-center gap-3 pl-1.5 pr-3 py-1.5 min-h-[44px] rounded-xl border text-left transition-colors',
                    selected
                      ? 'bg-champagne border-chocolate text-text-dark font-semibold'
                      : 'border-transparent text-text-medium hover:bg-champagne-light hover:text-text-dark',
                  ].join(' ')}
                >
                  <span className="w-9 h-9 shrink-0 rounded-lg bg-champagne-light flex items-center justify-center">
                    <img src={categoryIconSrc(index)} alt="" className="w-7 h-7 object-contain" />
                  </span>
                  <span className="text-sm flex-1 min-w-0">{category.name}</span>
                  {selected && (
                    <svg className="w-4 h-4 text-chocolate-dark shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="border-t border-champagne px-4 pt-3 pb-safe">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-chocolate-dark hover:bg-chocolate-deep text-white text-sm font-semibold min-h-[44px] py-3 rounded-lg transition-colors"
          >
            Pronto
          </button>
        </div>
      </div>
    </div>
  );
}

export function CategorySuggestions({ categories, filters }: { categories: StoreCategory[]; filters: CatalogFilters }) {
  const term = normalize(filters.searchInput.trim());
  if (term.length < 2) return null;

  const matches = categories
    .filter((c) => !filters.categoryIds.includes(c.id) && normalize(c.name).includes(term))
    .slice(0, 3);
  if (matches.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <span className="text-sm text-text-medium">Ver categoria:</span>
      {matches.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => filters.handleCategoryFromSearch([...filters.categoryIds, c.id])}
          className="inline-flex items-center min-h-[44px] lg:min-h-0 px-3 py-1 rounded-full bg-champagne border border-chocolate text-sm font-medium text-chocolate-dark hover:bg-champagne-light transition-colors"
        >
          {c.name}
        </button>
      ))}
    </div>
  );
}

interface CategoryStripProps {
  categories: StoreCategory[];
  filters: CatalogFilters;
}

export function CategoryStrip({ categories, filters }: CategoryStripProps) {
  const { ref, onMouseDown, onMouseMove, onMouseUp, onMouseLeave, onMouseEnter, onTouchStart, onTouchEnd, onClickCapture } =
    useDragScroll<HTMLDivElement>();

  const [showAll, setShowAll] = useState(false);

  if (categories.length === 0) return null;

  const featuredIds = new Set(pickFeaturedCategories(categories).map((c) => c.id));
  const shown = categories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => featuredIds.has(category.id) || filters.categoryIds.includes(category.id));
  const hasMore = shown.length < categories.length;

  const toggle = (id: number) => {
    filters.handleCategoryChange(
      filters.categoryIds.includes(id) ? filters.categoryIds.filter((c) => c !== id) : [...filters.categoryIds, id],
    );
  };

  return (
    <nav aria-label="Categorias" className="mb-4">
      <div
        ref={ref}
        className="flex gap-2 overflow-x-auto no-scrollbar pb-1 cursor-grab select-none [scroll-behavior:auto] lg:flex-wrap lg:overflow-visible lg:cursor-auto"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClickCapture={onClickCapture}
      >
        {shown.map(({ category, index }) => {
          const selected = filters.categoryIds.includes(category.id);
          return (
            <button
              key={category.id}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(category.id)}
              className={[
                'shrink-0 flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 min-h-[44px] rounded-full border text-sm whitespace-nowrap transition-colors',
                selected
                  ? 'bg-champagne border-chocolate text-text-dark font-semibold'
                  : 'bg-white border-champagne-dark text-text-medium hover:border-chocolate/60 hover:text-text-dark',
              ].join(' ')}
            >
              <img src={categoryIconSrc(index)} alt="" draggable={false} className="w-7 h-7 object-contain" />
              {category.name}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="shrink-0 flex items-center gap-2 px-4 py-1.5 min-h-[44px] rounded-full border border-chocolate text-sm font-semibold text-chocolate-dark whitespace-nowrap hover:bg-champagne-light transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          {hasMore ? `Ver todas (${categories.length})` : 'Buscar categoria'}
        </button>
      </div>
      {showAll && <AllCategoriesDialog categories={categories} filters={filters} onClose={() => setShowAll(false)} />}
    </nav>
  );
}

interface ActiveFiltersProps {
  colors: StoreColor[];
  filters: CatalogFilters;
  className?: string;
}

export function ActiveFilters({ colors, filters, className }: ActiveFiltersProps) {
  const { types } = useVariationCatalog();

  const items: { key: string; label: string; onRemove?: () => void }[] = [];

  if (filters.search) {
    items.push({ key: 'q', label: `Busca: ${filters.search}`, onRemove: () => filters.handleSearchChange('') });
  }
  const gender = genderLabel(filters.gender);
  if (gender) {
    items.push({ key: 'gender', label: gender, onRemove: () => filters.handleGenderChange(undefined) });
  }
  if (filters.hasPromotion) {
    items.push({
      key: 'promo',
      label: 'Promoção',
      onRemove: filters.promotionLocked ? undefined : () => filters.handlePromotionChange(false),
    });
  }
  const selectedType = types.find((t) => t.id === filters.variationTypeId);
  const optionLabels = new Map((selectedType?.options ?? []).map((o) => [o.code, o.label] as const));
  filters.variationValues.forEach((code) => {
    items.push({
      key: `var-${code}`,
      label: optionLabels.get(code) ?? code,
      onRemove: () => filters.handleVariationChange(filters.variationValues.filter((v) => v !== code)),
    });
  });
  if (filters.colorId !== undefined) {
    const color = colors.find((c) => c.id === filters.colorId);
    if (color) items.push({ key: 'color', label: color.name, onRemove: () => filters.handleColorChange(undefined) });
  }
  if (filters.minPriceInput !== '' || filters.maxPriceInput !== '') {
    const from = filters.minPriceInput !== '' ? `R$ ${filters.minPriceInput}` : 'R$ 0';
    const to = filters.maxPriceInput !== '' ? `R$ ${filters.maxPriceInput}` : 'sem limite';
    items.push({
      key: 'price',
      label: `${from} a ${to}`,
      onRemove: () => {
        filters.handleMinPriceChange('');
        filters.handleMaxPriceChange('');
      },
    });
  }

  if (items.length === 0) return null;

  return (
    <div className={['flex flex-wrap items-center gap-x-2 gap-y-1 lg:gap-y-2', className].filter(Boolean).join(' ')}>
      {items.map((item) =>
        item.onRemove ? (
          <button
            key={item.key}
            type="button"
            onClick={item.onRemove}
            aria-label={`Remover filtro ${item.label}`}
            className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 min-h-[44px] lg:min-h-0 rounded-full bg-champagne border border-champagne-dark text-sm text-text-dark hover:border-chocolate transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
          >
            {item.label}
            <svg className="w-3.5 h-3.5 text-text-medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <span key={item.key} className="inline-flex items-center px-3 py-1 rounded-full bg-champagne border border-champagne-dark text-sm text-text-dark">
            {item.label}
          </span>
        ),
      )}
    </div>
  );
}
